import { z } from 'zod'

/**
 * Lenient zod leaf wrappers, shared by every widget that consumes a settings
 * group defined outside its own config module.
 *
 * These four (plus the hex pattern) were private to `widgets/explorer/config.ts`
 * until #902 moved the card-skin leaves into `common/property-card/
 * card-skin-fields.ts` so the standalone card widgets can consume the same
 * definitions. A shared field set needs shared wrappers, and the shared module
 * has to stay a LEAF — importing the explorer's config would pull its
 * destinations, labels and sanitizer graph into every widget bundle that wants
 * a card skin.
 *
 * Only wrappers with a second consumer live here: the four the shared leaves
 * use, plus the byte-identical `lenientEnumDefault` copy amenities carried
 * since #1423, folded in. The rest (`lenientIntDefault`, `wpLenient*`,
 * `lenientStringLines`, …) are still declared in the explorer's config, where
 * their only callers live — moving them would be churn with no consumer on the
 * other side.
 *
 * Every wrapper below is byte-identical to the explorer-private original,
 * including the comments explaining WHY each one degrades rather than throws:
 * `exploreWidgetConfig.parse` runs at RENDER time, so a malformed stored value
 * must fall back to "unset", never crash the widget with a ZodError.
 */

/** Three- or six-digit hex, anchored. Shared with the explorer's own `lenientHexDefault`. */
export const HEX_COLOR_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/

/**
 * Lenient optional integer in [min, max]. Implemented as preprocess (not
 * `.catch`) so the zod-form UI typing keeps seeing the field through its usual
 * ZodEffects unwrap.
 */
export const lenientInt = (min: number, max: number) =>
  z.preprocess((v) => {
    // Only actual integers or digit-only strings pass — '20.9' and whitespace
    // degrade to unset, they are not truncated/coerced.
    const n = typeof v === 'number' && Number.isInteger(v) ? v
      : typeof v === 'string' && /^\d+$/.test(v) ? parseInt(v, 10)
      : undefined
    return n === undefined || n < min || n > max ? undefined : n
  }, z.number().int().min(min).max(max).optional())

/**
 * Optional hex colour for the card-template port (#872): absent = "inherit the
 * stylesheet", which is what keeps an unconfigured card pixel-identical to
 * today. A malformed value degrades to absent, never to a literal color.
 */
export const lenientHex = () =>
  z.preprocess((v) => (typeof v === 'string' && HEX_COLOR_RE.test(v.trim()) ? v.trim() : undefined), z.string().optional())

/**
 * Lenient boolean WITH a default. The group contract is "a fresh widget stores
 * only {} and parsing fills the rest"; malformed stored values degrade to the
 * default, never crash render.
 */
export const lenientBoolDefault = (def: boolean) =>
  z.preprocess((v) => (typeof v === 'boolean' ? v : undefined), z.boolean().default(def))

/**
 * Lenient enum WITH a default. The extra U type param is zod's own
 * literal-preserving trick (z.enum's signature): without it T widens to
 * string[] and every leaf infers `string`.
 */
export const lenientEnumDefault = <U extends string, T extends [U, ...U[]]>(values: T, def: NoInfer<T[number]>) =>
  z.preprocess((v) => (typeof v === 'string' && (values as readonly string[]).includes(v) ? v : undefined), z.enum(values).default(def))

/**
 * Lenient settings GROUP: anything that is not a plain object parses as the
 * inner schema's default. Two artifacts land here — PHP's json_encode
 * serializing a cleared `{}` as `[]` (the `phpObject` case, common/schema.ts),
 * and plain garbage (`0`, `"x"`, a non-empty array) left under a key by an
 * API/plugin writer or a pasted blob. For a group that is NEW on a widget
 * (#902: related-properties `card`) the second class matters: before the key
 * existed, `widgetSchema.extend` silently stripped it, and a widget that
 * rendered fine yesterday must not throw at render today because of a stale
 * value it never read. Same never-throw contract as every leaf wrapper above.
 * Put `.default({})`/`.describe()` INSIDE; chain nothing on the result.
 */
export const lenientObjectGroup = <T extends z.ZodTypeAny>(shape: T) =>
  z.preprocess((v) => (v !== null && typeof v === 'object' && !Array.isArray(v) ? v : undefined), shape)
